/**
 * /placements/live — upload a script, watch the agent read it, see the inventory.
 *
 * Separate page from /placements on purpose. The saved run and a live run produce nearly
 * identical tables, so having both behind one URL made "am I looking at the new results?"
 * genuinely unanswerable. Different URL, different thing.
 *
 * The results themselves are rendered by PlacementInventoryPage with a `data` prop — same
 * component as the saved run, so there is exactly one implementation of the table.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import RunScriptPanel from "@/components/placements/RunScriptPanel";
import PlacementInventoryPage from "@/pages/PlacementInventoryPage";
import type { PlacementRun } from "@/types/placement-run";

export default function PlacementLiveRunPage() {
  const [result, setResult] = useState<{ data: PlacementRun; at: Date } | null>(null);

  if (result) {
    return (
      <div>
        <div className="bg-slate-50 pt-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => setResult(null)}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Run another script
            </button>
          </div>
        </div>
        <PlacementInventoryPage data={result.data} finishedAt={result.at} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
            Placement agent · live run
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight">Run the agent</h1>
          <p className="mt-2 text-sm text-slate-600">
            Upload a screenplay. The agent splits it into scenes, reads each one three
            times, prices every product it finds, and shows you the inventory with the
            sentence each row came from.
          </p>
          <Link
            to="/placements"
            className="mt-3 inline-block text-sm text-slate-600 underline hover:text-slate-900"
          >
            or look at the saved Detours run
          </Link>
        </div>

        <RunScriptPanel onComplete={(data) => setResult({ data, at: new Date() })} />
      </div>
    </div>
  );
}
